import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent as MuiDialogContent,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Chip,
  Tooltip,
  Avatar,
  Paper,
  Tab,
  Tabs,
  DialogActions,
  Alert,
  styled,
  Stack,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  FormHelperText,
  InputLabel,
  Snackbar,
  LinearProgress
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Dashboard as DashboardIcon,
  ContentCopy as ContentCopyIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ObserverIcon,
  Person as MemberIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { projectService } from '../../services/project';
import { userService, UserSimplified } from '../../services/user';
import boardInviteService from '../../services/boardInvite';
import projectInviteService from '../../services/projectInvite';
import { useAuth } from '../../contexts/AuthContext';
import { BoardUser, BoardInvite, CreateBoardInvite } from '../../interfaces/boardInvite';
import { ProjectInviteDto } from '../../interfaces/projectInvite';
import NotificationDialog from '../notificationDialog';
import { useNotification } from '../../hooks/useNotification';
import ConfirmationDialog from '../confirmationDialog';

// Styled components
const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: 0,
  backgroundColor: '#2a2a3c',
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto', // scroll vertical
  maxHeight: '80vh' // define uma altura máxima (ajustável conforme necessário)
}));

const UserManagementDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#2a2a3c',
    color: '#ffffff',
    borderRadius: '8px',
    maxWidth: '900px',
    width: '100%',
    height: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  }
}));

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#384664',
  padding: '16px 24px',
  fontSize: '1.2rem',
  fontWeight: 600,
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#3ea6ff'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
  padding: '10px 16px',
  fontSize: '0.875rem',
  color: '#ffffff'
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#2a2a3c',
  '&:hover': {
    backgroundColor: 'rgba(62, 166, 255, 0.08)'
  }
}));

// Interfaces
interface UnifiedUserManagementProps {
  projectId: string;
  isAdmin: boolean;
  refreshProject: () => void;
  open: boolean;
  onClose: () => void;
  boards?: Array<{ id: string; title: string }>;
}

interface ProjectUser {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  isObserver: boolean;
  isCreator: boolean;
  isSystemAdmin?: boolean;
  boardAccess?: BoardAccess[];
}

interface BoardAccess {
  boardId: string;
  boardTitle: string;
  accessLevel: string;
  joinedAt: string;
  joinedViaInvite: boolean;
}

interface UnifiedUser extends ProjectUser {
  projectAccess: 'full' | 'boards-only';
  expandedBoards?: boolean;
}

const UnifiedUserManagement: React.FC<UnifiedUserManagementProps> = ({
  projectId,
  isAdmin,
  refreshProject,
  open,
  onClose,
  boards = []
}) => {
  const { user } = useAuth();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const isSystemAdminOrDeveloper = user?.role === 'Admin' || user?.role === 'Developer';
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Project users state
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [unifiedUsers, setUnifiedUsers] = useState<UnifiedUser[]>([]);
  const [systemUsers, setSystemUsers] = useState<UserSimplified[]>([]);
  const [loadingSystemUsers, setLoadingSystemUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Add user state
  const [selectedUser, setSelectedUser] = useState<UserSimplified | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('member');
  const [addToProject, setAddToProject] = useState(true);
  const [selectedBoards, setSelectedBoards] = useState<string[]>([]);
  const [boardAccessLevels, setBoardAccessLevels] = useState<Record<string, string>>({});

  // Invites state
  const [inviteType, setInviteType] = useState<'project' | 'board'>('project');
  const [selectedBoardForInvite, setSelectedBoardForInvite] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedUserForInvite, setSelectedUserForInvite] = useState<UserSimplified | null>(null);
  const [inviteAccessLevel, setInviteAccessLevel] = useState<'Member' | 'Administrator' | 'Observer'>('Member');
  const [inviteExpirationDays, setInviteExpirationDays] = useState(7);
  const [boardInvites, setBoardInvites] = useState<Record<string, BoardInvite[]>>({});
  const [projectInvites, setProjectInvites] = useState<ProjectInviteDto[]>([]);

  // Edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<string>('member');
  const [editingUser, setEditingUser] = useState<UnifiedUser | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editBoardAccess, setEditBoardAccess] = useState<Record<string, string>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UnifiedUser | null>(null);
  
  // Estado para confirmação de exclusão de convite
  const [deleteInviteConfirmOpen, setDeleteInviteConfirmOpen] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState<{type: 'project' | 'board', id: string, inviteId: string, email: string} | null>(null);

  // Expanded users state for board details
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);


      // Load project members
      const members = await projectService.getProjectMembers(projectId);
      const transformedUsers = members.map((member: any) => ({
        id: member.id,
        name: member.firstName && member.lastName 
          ? `${member.firstName} ${member.lastName}`.trim() 
          : member.name || member.username || member.email.split('@')[0],
        email: member.email,
        isAdmin: !!member.isAdmin,
        isObserver: !!member.isObserver,
        isCreator: !!member.isCreator,
        isSystemAdmin: !!member.isSystemAdmin
      }));
      setProjectUsers(transformedUsers);

      // Load board users for each board
      const allBoardUsers: Record<string, BoardUser[]> = {};
      const allBoardInvites: Record<string, BoardInvite[]> = {};
      
      for (const board of boards) {
        try {
          const boardUsers = await boardInviteService.getBoardUsers(board.id);
          allBoardUsers[board.id] = boardUsers;
          
          if (isAdmin) {
            const invites = await boardInviteService.getBoardInvites(board.id);
            allBoardInvites[board.id] = invites;
          }
        } catch (error) {
          console.error(`Error loading board ${board.id} data:`, error);
        }
      }

      setBoardInvites(allBoardInvites);

      // Load project invites
      if (isAdmin) {
        try {
          const pInvites = await projectInviteService.getProjectInvites(projectId);
          setProjectInvites(Array.isArray(pInvites) ? pInvites : []);
        } catch (error) {
          console.error('Error loading project invites:', error);
          setProjectInvites([]);
        }
      }

      // Create unified user list
      const unified: UnifiedUser[] = [];
      const processedEmails = new Set<string>();

      // Add project users
      transformedUsers.forEach((projectUser: ProjectUser) => {
        const boardAccessList: BoardAccess[] = [];
        
        // Check which boards this user has access to
        for (const board of boards) {
          const boardUser = allBoardUsers[board.id]?.find(
            bu => bu.email.toLowerCase() === projectUser.email.toLowerCase()
          );
          
          if (boardUser) {
            boardAccessList.push({
              boardId: board.id,
              boardTitle: board.title,
              accessLevel: boardUser.accessLevel,
              joinedAt: boardUser.joinedAt,
              joinedViaInvite: boardUser.joinedViaInvite
            });
          }
        }

        unified.push({
          ...projectUser,
          projectAccess: 'full',
          boardAccess: boardAccessList
        });
        processedEmails.add(projectUser.email.toLowerCase());
      });

      // Add board-only users
      for (const board of boards) {
        const boardUsers = allBoardUsers[board.id] || [];
        
        for (const boardUser of boardUsers) {
          if (!processedEmails.has(boardUser.email.toLowerCase())) {
            // This user is not in the project, only in boards
            let existingUser = unified.find(u => u.email.toLowerCase() === boardUser.email.toLowerCase());
            
            if (!existingUser) {
              existingUser = {
                id: boardUser.userId,
                name: `${boardUser.firstName} ${boardUser.lastName}`.trim() || boardUser.username,
                email: boardUser.email,
                isAdmin: false,
                isObserver: false,
                isCreator: false,
                projectAccess: 'boards-only',
                boardAccess: []
              };
              unified.push(existingUser);
            }
            
            existingUser.boardAccess!.push({
              boardId: board.id,
              boardTitle: board.title,
              accessLevel: boardUser.accessLevel,
              joinedAt: boardUser.joinedAt,
              joinedViaInvite: boardUser.joinedViaInvite
            });
          }
        }
      }

      setUnifiedUsers(unified);
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Falha ao carregar dados dos usuários');
    } finally {
      setLoading(false);
    }
  }, [projectId, boards, isAdmin]);

  // Load system users
  const loadSystemUsers = useCallback(async () => {
    try {
      setLoadingSystemUsers(true);
      const data = await userService.getSimplifiedUsers();
      setSystemUsers(data);
    } catch (error) {
      console.error('Error loading system users:', error);
    } finally {
      setLoadingSystemUsers(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadData();
      loadSystemUsers();
    } else {
      setSearchQuery('');
      setExpandedUsers(new Set());
    }
  }, [open, loadData, loadSystemUsers]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return unifiedUsers;
    }
    
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return unifiedUsers.filter(user => 
      user.name.toLowerCase().includes(normalizedQuery) || 
      user.email.toLowerCase().includes(normalizedQuery)
    );
  }, [unifiedUsers, searchQuery]);

  // Available users for adding
  const availableUsers = useMemo(() => {
    const existingEmails = new Set(unifiedUsers.map(user => user.email.toLowerCase()));
    return systemUsers.filter(user => !existingEmails.has(user.email.toLowerCase()));
  }, [systemUsers, unifiedUsers]);

  // Utility functions
  const stringToColor = (string: string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAccessLevelIcon = (level: string) => {
    switch (level) {
      case 'Administrator':
        return <AdminIcon fontSize="small" />;
      case 'Observer':
        return <ObserverIcon fontSize="small" />;
      default:
        return <MemberIcon fontSize="small" />;
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'Administrator':
        return '#f44336';
      case 'Observer':
        return '#e0e0e0';
      default:
        return '#4caf50';
    }
  };

  // Toggle expanded state for user
  const toggleUserExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Handle add user
  const handleAddUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);

      if (addToProject) {
        // Add to project
        const isAdminRole = selectedRole === 'admin';
        const isObserverRole = selectedRole === 'observer';
        await projectService.addUserToProject(projectId, selectedUser.id, isAdminRole, isObserverRole);
      } else {
        // Add to specific boards only
        for (const boardId of selectedBoards) {
          const accessLevel = boardAccessLevels[boardId] || 'Member';
          
          try {
            // Add user directly to board
            await boardInviteService.addUserDirectlyToBoard(boardId, selectedUser.id, accessLevel);
          } catch (error: any) {
            // If the user already has access to this board, continue with the next one
            if (error.response?.status === 409) {
              continue;
            }
            throw error;
          }
        }
      }

      showSuccess('Usuário adicionado com sucesso!');
      await loadData();
      refreshProject();

      // Reset form
      setSelectedUser(null);
      setSelectedRole('member');
      setAddToProject(true);
      setSelectedBoards([]);
      setBoardAccessLevels({});
      setTabValue(0);
    } catch (error: any) {
      console.error('Error adding user:', error);
      showError(error.response?.data?.message || 'Falha ao adicionar usuário');
    } finally {
      setLoading(false);
    }
  };

  // Handle create invite (project or board)
  const handleCreateInvite = async () => {
    if (!selectedUserForInvite) return;
    if (inviteType === 'board' && !selectedBoardForInvite) return;

    try {
      setLoading(true);

      if (inviteType === 'project') {
        await projectInviteService.createInvite(projectId, {
          email: selectedUserForInvite.email,
          accessLevel: inviteAccessLevel,
          expirationDays: inviteExpirationDays
        });
      } else {
        await boardInviteService.createInvite(selectedBoardForInvite, {
          email: selectedUserForInvite.email,
          accessLevel: inviteAccessLevel,
          expirationDays: inviteExpirationDays
        });
      }

      showSuccess('Convite criado com sucesso!');
      await loadData();

      // Reset form
      setSelectedUserForInvite(null);
      setInviteAccessLevel('Member');
      setInviteExpirationDays(7);
    } catch (error: any) {
      console.error('Error creating invite:', error);
      showError(error.response?.data?.message || 'Falha ao criar convite');
    } finally {
      setLoading(false);
    }
  };

  // Copy invite link
  const copyInviteLink = (inviteCode: string, type: 'project' | 'board' = 'board') => {
    const inviteUrl = `${window.location.origin}/invite/${type}/${inviteCode}`;
    navigator.clipboard.writeText(inviteUrl);
    showSuccess('Link do convite copiado!');
  };

  // Handle delete invite com confirmação
  const handleDeleteInvite = async (type: 'project' | 'board', id: string, inviteId: string, email: string) => {
    setInviteToDelete({ type, id, inviteId, email });
    setDeleteInviteConfirmOpen(true);
  };
  
  const confirmDeleteInvite = async () => {
    if (!inviteToDelete) return;
    
    try {
      if (inviteToDelete.type === 'project') {
        await projectInviteService.deleteInvite(projectId, inviteToDelete.inviteId);
      } else {
        await boardInviteService.deleteInvite(inviteToDelete.id, inviteToDelete.inviteId);
      }
      showSuccess('Convite excluído com sucesso!');
      await loadData();
      setDeleteInviteConfirmOpen(false);
      setInviteToDelete(null);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Falha ao excluir convite');
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      setLoading(true);

      // Se o usuário tem acesso ao projeto completo, atualizar a função no projeto
      if (editingUser.projectAccess === 'full') {
        const isAdminRole = editingRole === 'admin';
        const isObserverRole = editingRole === 'observer';
        
        await projectService.changeUserRole(projectId, editingUser.id, isAdminRole, isObserverRole);
      }

      // Atualizar acessos aos boards
      for (const board of boards) {
        const currentAccess = editingUser.boardAccess?.find(ba => ba.boardId === board.id);
        const newAccessLevel = editBoardAccess[board.id];

        if (currentAccess && newAccessLevel && currentAccess.accessLevel !== newAccessLevel) {
          // Atualizar nível de acesso existente
          await boardInviteService.updateBoardUserAccess(board.id, editingUser.id, {
            accessLevel: newAccessLevel as any
          });
        } else if (!currentAccess && newAccessLevel) {
          // Adicionar novo acesso ao board
          await boardInviteService.addUserDirectlyToBoard(board.id, editingUser.id, newAccessLevel);
        } else if (currentAccess && !newAccessLevel) {
          // Remover acesso ao board
          await boardInviteService.removeBoardUser(board.id, editingUser.id);
        }
      }

      showSuccess('Usuário atualizado com sucesso!');
      await loadData();
      refreshProject();
      
      // Fechar diálogo e limpar estado
      setEditDialogOpen(false);
      setEditingUser(null);
      setEditingUserId(null);
      setEditBoardAccess({});
    } catch (error: any) {
      console.error('Error updating user:', error);
      showError(error.response?.data?.message || 'Falha ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  // Render user row
  const renderUserRow = (user: UnifiedUser, index: number) => {
    const isExpanded = expandedUsers.has(user.id);
    const hasBoardAccess = user.boardAccess && user.boardAccess.length > 0;

    return (
      <React.Fragment key={user.id}>
        <StyledTableRow>
          <StyledTableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasBoardAccess && (
                <IconButton
                  size="small"
                  onClick={() => toggleUserExpanded(user.id)}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
              <Avatar
                sx={{
                  bgcolor: stringToColor(user.name),
                  width: 32,
                  height: 32,
                  fontSize: '0.875rem'
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {user.name}
                  {user.isCreator && (
                    <Chip 
                      label="Criador"
                      size="small"
                      sx={{
                        ml: 1,
                        height: '20px',
                        backgroundColor: 'rgba(255, 193, 7, 0.2)',
                        color: '#ffc107'
                      }}
                    />
                  )}
                  {user.isSystemAdmin && (
                    <Chip 
                      label="Admin Sistema"
                      size="small"
                      sx={{
                        ml: 1,
                        height: '20px',
                        backgroundColor: '#6A1B9A',
                        color: 'white'
                      }}
                    />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </StyledTableCell>
          
          <StyledTableCell>
            {user.projectAccess === 'full' ? (
              <Chip
                icon={<GroupIcon />}
                label={user.isAdmin ? 'Administrador' : user.isObserver ? 'Observador' : 'Membro'}
                size="small"
                sx={{
                  backgroundColor: user.isAdmin ? 'rgba(62, 166, 255, 0.2)' : 
                    user.isObserver ? 'rgba(255, 255, 255, 0.1)' : 'rgba(76, 175, 80, 0.2)',
                  color: user.isAdmin ? '#3ea6ff' : 
                    user.isObserver ? '#e0e0e0' : '#4caf50'
                }}
              />
            ) : (
              <Chip
                icon={<DashboardIcon />}
                label="Apenas Boards"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  color: '#ff9800'
                }}
              />
            )}
          </StyledTableCell>
          
          <StyledTableCell>
            {hasBoardAccess ? (
              <Typography variant="body2">
                {user.boardAccess!.length} board{user.boardAccess!.length !== 1 ? 's' : ''}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sem acesso a boards
              </Typography>
            )}
          </StyledTableCell>
          
          <StyledTableCell align="right">
            {isAdmin && !user.isCreator && (
              <>
                <Tooltip title="Editar">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingUser(user);
                      setEditingUserId(user.id);
                      setEditingRole(user.isAdmin ? 'admin' : user.isObserver ? 'observer' : 'member');
                      // Preparar níveis de acesso dos boards
                      const boardAccessMap: Record<string, string> = {};
                      if (user.boardAccess) {
                        user.boardAccess.forEach(access => {
                          boardAccessMap[access.boardId] = access.accessLevel;
                        });
                      }
                      setEditBoardAccess(boardAccessMap);
                      setEditDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setUserToDelete(user);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </StyledTableCell>
        </StyledTableRow>
        
        {isExpanded && hasBoardAccess && (
          <TableRow>
            <TableCell colSpan={4} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)', py: 1 }}>
              <List dense sx={{ pl: 7 }}>
                {user.boardAccess!.map((access, idx) => (
                  <React.Fragment key={access.boardId}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 28, height: 28, backgroundColor: 'rgba(62, 166, 255, 0.2)' }}>
                          <DashboardIcon fontSize="small" sx={{ color: '#3ea6ff' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={access.boardTitle}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getAccessLevelIcon(access.accessLevel)}
                            <Typography variant="caption" sx={{ color: getAccessLevelColor(access.accessLevel) }}>
                              {access.accessLevel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              • Entrou em {new Date(access.joinedAt).toLocaleDateString('pt-BR')}
                              {access.joinedViaInvite && ' (via convite)'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < user.boardAccess!.length - 1 && <Divider variant="inset" />}
                  </React.Fragment>
                ))}
              </List>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <UserManagementDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogHeader>
          <Typography variant="h6">Gerenciamento de Usuários</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#384664' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <StyledTab icon={<PeopleIcon />} label="Usuários" />
            {isAdmin && <StyledTab icon={<ContentCopyIcon />} label="Convidar Usuários" />}
            {isSystemAdminOrDeveloper && <StyledTab icon={<PersonAddIcon />} label="Adicionar Usuários" />}
          </Tabs>
        </Box>

        <DialogContent>
          {/* Users Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Buscar por nome ou email..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Usuário</StyledTableCell>
                        <StyledTableCell>Acesso ao Projeto</StyledTableCell>
                        <StyledTableCell>Acesso a Boards</StyledTableCell>
                        <StyledTableCell align="right">Ações</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.map((user, index) => renderUserRow(user, index))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Add Users Tab - Only System Admin and Developer */}
          {tabValue === 2 && isSystemAdminOrDeveloper && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                Como Administrador do Sistema ou Desenvolvedor, você pode adicionar usuários diretamente ao projeto ou a boards específicos.
              </Typography>

              {availableUsers.length === 0 && !loadingSystemUsers && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Todos os usuários do sistema já estão no projeto. Não há usuários disponíveis para adicionar.
                </Alert>
              )}

              <Autocomplete
                fullWidth
                options={availableUsers}
                loading={loadingSystemUsers}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                noOptionsText="Todos os usuários já estão no projeto"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecionar Usuário"
                    variant="outlined"
                    InputLabelProps={{
                      sx: { 
                        backgroundColor: '#2a2a3c',
                        px: 0.5,
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }
                    }}
                    sx={{ mb: 3 }}
                  />
                )}
              />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel 
                  sx={{ 
                    backgroundColor: '#2a2a3c',
                    px: 0.5,
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    }
                  }}
                >
                  Adicionar ao
                </InputLabel>
                <Select
                  value={addToProject ? 'project' : 'boards'}
                  onChange={(e) => setAddToProject(e.target.value === 'project')}
                >
                  <MenuItem value="project">Projeto Inteiro</MenuItem>
                  <MenuItem value="boards">Apenas Boards Específicos</MenuItem>
                </Select>
              </FormControl>

              {addToProject ? (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel
                    sx={{ 
                      backgroundColor: '#2a2a3c',
                      px: 0.5,
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)'
                      }
                    }}
                  >
                    Função no Projeto
                  </InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    <MenuItem value="member">Membro</MenuItem>
                    <MenuItem value="admin">Administrador</MenuItem>
                    <MenuItem value="observer">Observador</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <>
                  <Autocomplete
                    multiple
                    options={boards}
                    getOptionLabel={(option) => option.title}
                    value={boards.filter(b => selectedBoards.includes(b.id))}
                    onChange={(_, newValue) => {
                      setSelectedBoards(newValue.map(b => b.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Selecionar Boards"
                        variant="outlined"
                        InputLabelProps={{
                          sx: { 
                            backgroundColor: '#2a2a3c',
                            px: 0.5,
                            '&.MuiInputLabel-shrink': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }
                        }}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  {selectedBoards.map(boardId => {
                    const board = boards.find(b => b.id === boardId);
                    return (
                      <FormControl key={boardId} fullWidth sx={{ mb: 2 }}>
                        <InputLabel
                          sx={{ 
                            backgroundColor: '#2a2a3c',
                            px: 0.5,
                            '&.MuiInputLabel-shrink': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }}
                        >
                          {board?.title} - Nível de Acesso
                        </InputLabel>
                        <Select
                          value={boardAccessLevels[boardId] || 'Member'}
                          onChange={(e) => setBoardAccessLevels(prev => ({
                            ...prev,
                            [boardId]: e.target.value
                          }))}
                        >
                          <MenuItem value="Member">Membro</MenuItem>
                          <MenuItem value="Administrator">Administrador</MenuItem>
                          <MenuItem value="Observer">Observador</MenuItem>
                        </Select>
                      </FormControl>
                    );
                  })}
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddUser}
                disabled={!selectedUser || loading || (!addToProject && selectedBoards.length === 0)}
                startIcon={<PersonAddIcon />}
                sx={{ mt: 2 }}
              >
                Adicionar Usuário
              </Button>
            </Box>
          )}

          {/* Invites Tab - Only Project Admins */}
          {tabValue === 1 && isAdmin && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Criar Convite</Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                Como Administrador do Projeto, você pode criar convites para usuários entrarem no projeto ou em boards específicos.
              </Typography>
              
              <Stack spacing={2} sx={{ mb: 4 }}>
                <FormControl fullWidth>
                  <InputLabel
                    sx={{ 
                      backgroundColor: '#2a2a3c',
                      px: 0.5,
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)'
                      }
                    }}
                  >
                    Tipo de Convite
                  </InputLabel>
                  <Select
                    value={inviteType}
                    onChange={(e) => {
                      setInviteType(e.target.value as 'project' | 'board');
                      setSelectedBoardForInvite('');
                    }}
                  >
                    <MenuItem value="project">Convite para Projeto</MenuItem>
                    <MenuItem value="board">Convite para Board</MenuItem>
                  </Select>
                </FormControl>

                {inviteType === 'board' && (
                  <FormControl fullWidth>
                    <InputLabel
                      sx={{ 
                        backgroundColor: '#2a2a3c',
                        px: 0.5,
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }}
                    >
                      Board
                    </InputLabel>
                    <Select
                      value={selectedBoardForInvite}
                      onChange={(e) => setSelectedBoardForInvite(e.target.value)}
                    >
                      {boards.map(board => (
                        <MenuItem key={board.id} value={board.id}>
                          {board.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Autocomplete
                  fullWidth
                  options={availableUsers}
                  loading={loadingSystemUsers}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={selectedUserForInvite}
                  onChange={(_, newValue) => setSelectedUserForInvite(newValue)}
                  noOptionsText="Todos os usuários já estão no projeto"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecionar Usuário"
                      variant="outlined"
                      InputLabelProps={{
                        sx: { 
                          backgroundColor: '#2a2a3c',
                          px: 0.5,
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  )}
                />

                <Stack direction="row" spacing={2}>
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel
                      sx={{ 
                        backgroundColor: '#2a2a3c',
                        px: 0.5,
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }}
                    >
                      Nível de Acesso
                    </InputLabel>
                    <Select
                      value={inviteAccessLevel}
                      onChange={(e) => setInviteAccessLevel(e.target.value as any)}
                    >
                      <MenuItem value="Observer">Observador</MenuItem>
                      <MenuItem value="Member">Membro</MenuItem>
                      <MenuItem value="Administrator">Administrador</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel
                      sx={{ 
                        backgroundColor: '#2a2a3c',
                        px: 0.5,
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }}
                    >
                      Validade
                    </InputLabel>
                    <Select
                      value={inviteExpirationDays}
                      onChange={(e) => setInviteExpirationDays(Number(e.target.value))}
                    >
                      <MenuItem value={1}>1 dia</MenuItem>
                      <MenuItem value={7}>7 dias</MenuItem>
                      <MenuItem value={30}>30 dias</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Button
                  variant="contained"
                  onClick={handleCreateInvite}
                  disabled={!selectedUserForInvite || loading || (inviteType === 'board' && !selectedBoardForInvite)}
                  startIcon={<PersonAddIcon />}
                >
                  Criar Convite
                </Button>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  flex: '1 1 auto',       // permite que cresça dentro do layout flex
                  maxHeight: '30vh',      // limite de altura ajustável
                  overflowY: 'auto',      // ativa scroll vertical
                  pr: 2                   // padding direito para compensar a barra de rolagem, opcional
                }}
              >

              <Typography variant="h6" sx={{ mb: 2 }}>Convites Ativos</Typography>
              
              {/* Project Invites */}
              {Array.isArray(projectInvites) && projectInvites.filter(inv => !inv.isUsed && inv.isValid).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" />
                    Convites para o Projeto
                  </Typography>
                  <List>
                    {projectInvites
                      .filter(inv => !inv.isUsed && inv.isValid)
                      .map(invite => (
                        <ListItem key={invite.id}>
                          <ListItemText
                            primary={invite.email}
                            secondary={`${invite.accessLevel} • Expira em ${new Date(invite.expiresAt).toLocaleDateString('pt-BR')}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => copyInviteLink(invite.inviteCode, 'project')}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteInvite('project', projectId, invite.id, invite.email)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Box>
              )}
              
              {/* Board Invites */}
              {boards.map(board => {
                const invites = boardInvites[board.id] || [];
                const activeInvites = invites.filter(inv => !inv.isUsed && inv.isValid);
                
                if (activeInvites.length === 0) return null;
                
                return (
                  <Box key={board.id} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DashboardIcon fontSize="small" />
                      {board.title}
                    </Typography>
                    <List>
                      {activeInvites.map(invite => (
                        <ListItem key={invite.id}>
                          <ListItemText
                            primary={invite.email}
                            secondary={`${invite.accessLevel} • Expira em ${new Date(invite.expiresAt).toLocaleDateString('pt-BR')}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => copyInviteLink(invite.inviteCode, 'board')}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteInvite('board', board.id, invite.id, invite.email)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })}
              </Box>
            </Box>
          )}
        </DialogContent>
      </UserManagementDialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2a2a3c',
            color: '#ffffff'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Editar Usuário</Typography>
            <IconButton size="small" onClick={() => setEditDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <MuiDialogContent sx={{ pb: 2 }}>
          {editingUser && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {editingUser.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {editingUser.email}
                </Typography>
              </Box>

              {editingUser.projectAccess === 'full' ? (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Este usuário tem acesso completo ao projeto. Altere sua função no projeto ou gerencie seus acessos aos boards individuais.
                    </Typography>
                  </Alert>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Função no Projeto</Typography>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Select
                      value={editingRole}
                      onChange={(e) => setEditingRole(e.target.value)}
                      disabled={editingUser.isCreator}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3ea6ff'
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5
                        }
                      }}
                    >
                      <MenuItem value="member">Membro</MenuItem>
                      <MenuItem value="admin">Administrador</MenuItem>
                      <MenuItem value="observer">Observador</MenuItem>
                    </Select>
                    {editingUser.isCreator && (
                      <FormHelperText>O criador do projeto não pode ter sua função alterada</FormHelperText>
                    )}
                  </FormControl>
                </>
              ) : (
                <Box>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Este usuário tem acesso apenas a boards específicos. Você pode gerenciar os acessos aos boards ou adicionar ao projeto completo.
                    </Typography>
                  </Alert>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 3 }}
                    onClick={async () => {
                      try {
                        setLoading(true);
                        // Adicionar usuário ao projeto como membro
                        await projectService.addUserToProject(projectId, editingUser.id, false, false);
                        showSuccess('Usuário adicionado ao projeto com sucesso!');
                        await loadData();
                        setEditDialogOpen(false);
                      } catch (error: any) {
                        showError(error.response?.data?.message || 'Falha ao adicionar usuário ao projeto');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <PersonAddIcon sx={{ mr: 1 }} />
                    Adicionar ao Projeto Completo
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DashboardIcon fontSize="small" />
                <Typography variant="subtitle2">Acesso aos Boards</Typography>
              </Box>
              
              {boards.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum board disponível no projeto
                </Typography>
              ) : (
                <Box sx={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  p: 1
                }}>
                  {boards.map(board => {
                    const currentAccess = editingUser.boardAccess?.find(ba => ba.boardId === board.id);
                    const newAccess = editBoardAccess[board.id];
                    const hasChanged = currentAccess ? currentAccess.accessLevel !== newAccess : !!newAccess;
                    
                    return (
                      <Box key={board.id} sx={{ 
                        mb: 1,
                        p: 1,
                        borderRadius: '4px',
                        backgroundColor: hasChanged ? 'rgba(62, 166, 255, 0.05)' : 'transparent'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {board.title}
                            </Typography>
                            {hasChanged && (
                              <Chip
                                label="modificado"
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '0.65rem',
                                  backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                  color: '#ffc107'
                                }}
                              />
                            )}
                            {editingUser.boardAccess?.some(ba => ba.boardId === board.id && ba.joinedViaInvite) && (
                              <Chip
                                label="via convite"
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '0.65rem',
                                  backgroundColor: 'rgba(62, 166, 255, 0.2)',
                                  color: '#3ea6ff'
                                }}
                              />
                            )}
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={editBoardAccess[board.id] || ''}
                              onChange={(e) => setEditBoardAccess(prev => ({
                                ...prev,
                                [board.id]: e.target.value
                              }))}
                              displayEmpty
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.2)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#3ea6ff'
                                }
                              }}
                            >
                              <MenuItem value="">Sem acesso</MenuItem>
                              <MenuItem value="Member">Membro</MenuItem>
                              <MenuItem value="Administrator">Administrador</MenuItem>
                              <MenuItem value="Observer">Observador</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </MuiDialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
            setEditingUserId(null);
            setEditBoardAccess({});
          }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit}
            disabled={loading || (!editingUser)}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#2a2a3c',
            color: '#ffffff'
          }
        }}
      >
        <DialogTitle>Confirmar Remoção</DialogTitle>
        <MuiDialogContent>
          <Typography>
            Tem certeza que deseja remover <strong>{userToDelete?.name}</strong> do projeto?
          </Typography>
          {userToDelete?.boardAccess && userToDelete.boardAccess.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Este usuário tem acesso a {userToDelete.boardAccess.length} board(s). 
              Ele perderá acesso a todos os boards também.
            </Alert>
          )}
        </MuiDialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={async () => {
              if (userToDelete) {
                try {
                  await projectService.removeUserFromProject(projectId, userToDelete.id);
                  showSuccess('Usuário removido com sucesso');
                  await loadData();
                  refreshProject();
                } catch (error: any) {
                  showError(error.response?.data?.message || 'Falha ao remover usuário');
                }
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
              }
            }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação para exclusão de convite */}
      <ConfirmationDialog
        open={deleteInviteConfirmOpen}
        onClose={() => {
          setDeleteInviteConfirmOpen(false);
          setInviteToDelete(null);
        }}
        onConfirm={confirmDeleteInvite}
        title="Excluir Convite"
        message={`Tem certeza que deseja excluir o convite para ${inviteToDelete?.email}?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="warning"
        confirmButtonColor="error"
      />
      
      {/* Notification Dialog */}
      <NotificationDialog
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </>
  );
};

export default UnifiedUserManagement;